import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import HomePage from '../pages/home/ui/HomePage';
import StorePage from '../pages/store/ui/StorePage';
import ProductDetailsPage from '../pages/product-details/ui/ProductDetailsPage';
import WishlistPage from '../pages/wishlist/ui/WishlistPage';
import WorkshopPage from '../pages/workshop/ui/WorkshopPage';
import ServiceDetailPage from '../pages/service-detail/ui/ServiceDetailPage';
import MobilityPage from '../pages/mobility/ui/MobilityPage';
import BudgetPage from '../pages/budget/ui/BudgetPage';
import WeCallPage from '../pages/wecall/ui/WeCallPage';
import RecruitmentPage from '../pages/recruitment/ui/RecruitmentPage';
import ContactPage from '../pages/contact/ui/ContactPage';
import CookiesPolicyPage from '../pages/cookies-policy/ui/CookiesPolicyPage';
import TermsConditionsPage from '../pages/terms-conditions/ui/TermsConditionsPage';
import PrivacyPolicyPage from '../pages/privacy-policy/ui/PrivacyPolicyPage';
import NotFoundPage from '../pages/not-found/ui/NotFoundPage';
import CookieConsent from '../components/cookie-consent/ui/CookieConsent';
import SearchPopup from '../components/search-popup/ui/SearchPopup';
import { AdminAuthProvider } from '../../context/AdminAuthContext';
import { ROUTES } from './Paths';

const AdminLoginPage = lazy(() => import('../pages/admin/ui/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('../pages/admin/ui/AdminDashboardPage'));
const AdminProductsPage = lazy(() => import('../pages/admin/ui/AdminProductsPage'));
const AdminCatalogPage = lazy(() => import('../pages/admin/ui/AdminCatalogPage'));
const AdminVideosPage = lazy(() => import('../pages/admin/ui/AdminVideosPage'));
const AdminHorarioPage = lazy(() => import('../pages/admin/ui/AdminHorarioPage'));
const AdminPricingsPage = lazy(() => import('../pages/admin/ui/AdminPricingsPage'));
const ProtectedRoute = lazy(() => import('../pages/admin/components/ProtectedRoute'));

// Component to scroll to top on route changes
const ScrollToTop: React.FC = () => {
    const location = useLocation();

    useEffect(() => {
        // Scroll to top when route changes
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant' // Use 'instant' for immediate scroll, or 'smooth' for animated scroll
        });
    }, [location.pathname, location.search]);

    return null;
};

const AdminFallback: React.FC = () => (
    <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#64748b',
        fontWeight: 600,
        background: '#f8fafc',
    }}>
        Loading admin...
    </div>
);

const AdminRouteScope: React.FC = () => (
    <AdminAuthProvider>
        <Suspense fallback={<AdminFallback />}>
            <Outlet />
        </Suspense>
    </AdminAuthProvider>
);

const Router: React.FC = () => {
    return (
        <BrowserRouter>
                <ScrollToTop />
        <Routes>
            <Route path={ROUTES.HOME} element={<HomePage />} />
                <Route path={ROUTES.STORE} element={<StorePage />} />
                <Route path="/loja/:categorySlug" element={<StorePage />} />
                <Route path="/produto/:id" element={<ProductDetailsPage />} />
                <Route path={ROUTES.WISHLIST} element={<WishlistPage />} />
                <Route path={ROUTES.WORKSHOP} element={<WorkshopPage />} />
                
                {/* Service Detail Routes - All under /oficina/ */}
                <Route path="/oficina/:serviceSlug" element={<ServiceDetailPage />} />
                
                <Route path={ROUTES.MOBILITY} element={<MobilityPage />} />
                <Route path={ROUTES.BUDGET} element={<BudgetPage />} />
                <Route path={ROUTES.WE_CALL} element={<WeCallPage />} />
                <Route path={ROUTES.RECRUITMENT} element={<RecruitmentPage />} />
                <Route path={ROUTES.CONTACT} element={<ContactPage />} />
                <Route path={ROUTES.COOKIES_POLICY} element={<CookiesPolicyPage />} />
                <Route path={ROUTES.TERMS} element={<TermsConditionsPage />} />
                <Route path={ROUTES.PRIVACY} element={<PrivacyPolicyPage />} />
                
                {/* Admin Routes */}
                <Route path={ROUTES.ADMIN} element={<AdminRouteScope />}>
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="login" element={<AdminLoginPage />} />
                    <Route
                        path="dashboard"
                        element={
                            <ProtectedRoute>
                                <AdminDashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="products"
                        element={
                            <ProtectedRoute>
                                <AdminProductsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="catalog"
                        element={
                            <ProtectedRoute>
                                <AdminCatalogPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="videos"
                        element={
                            <ProtectedRoute>
                                <AdminVideosPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="horario"
                        element={
                            <ProtectedRoute>
                                <AdminHorarioPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="pricings"
                        element={
                            <ProtectedRoute>
                                <AdminPricingsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<Navigate to="dashboard" replace />} />
                </Route>
                
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
            <SearchPopup />
            <CookieConsent />
        </BrowserRouter>
    );
};

export default Router;
